import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import {
    ColContainer,
    LoadingSpinner,
    RowContainer,
    SelectList,
    Typography,
} from '../../../components/common';
import { Link } from 'react-router-dom';
import IconComponent from '../../../components/Icon';
import Avatar from '../../../components/User/Avatar';
import { PrevDayLatestCell, ScheduleCells } from './scheduleCellRenderers';
import { TableTooltip } from './tableTooltip';

const TeamScheduleBreakdown = ({
    routingProfiles,
    instances,
    schedule,
    team,
    teams,
    allUsers,
    addUserToGroup,
    removeUserFromGroup,
    isLoading,
}) => {
    const [teamMembers, setTeamMembers] = useState(teams?.find((t) => t.id === team)?.array_agg);
    const [teamMembersNotInList, setTeamMembersNotInList] = useState([]);
    const dayStart = 480,
        dayEnd = 1260;

    useEffect(() => {
        setTeamMembers(teams?.find((t) => t.id === team)?.array_agg?.filter((a) => a[0]));
    }, [teams, team]);

    useEffect(() => {
        setTeamMembersNotInList(
            allUsers.filter((user) => !(teamMembers || []).map((v) => v[0]).includes(user.email))
        );
    }, [teamMembers, allUsers]);

    const todaysDayName = new Date().toLocaleString('en-uk', { weekday: 'long' }).split(' ')[0];

    return (
        <>
            <TableTooltip />
            <div className="w-tlSched">
                {!teams || teams.length === 0 || team === 0 ? (
                    <Typography variant="heading2.2">You do not have any teams assigned</Typography>
                ) : (
                    <div className="relative">
                        {isLoading && (
                            <div className="bg-gray-150 absolute h-full w-full py-12 opacity-70">
                                <LoadingSpinner />
                            </div>
                        )}
                        <table className="w-full table-fixed">
                            <thead>
                                {/* Formatter that controls the width of all of the table columns. */}
                                <tr key="table-formatter" className="h-0">
                                    <td className="h-0 w-60"></td>
                                    {Array.apply(null, Array((dayEnd - dayStart) / 15)).map(
                                        (_cell, index) => (
                                            <td key={`formatter-${index}`} className="h-0" />
                                        )
                                    )}
                                </tr>
                                {/* Date time values */}
                                <tr key="table-header" className="h-10">
                                    <th className="border border-t-0 border-l-0 border-table-border font-normal">
                                        <RowContainer spacing="medium" className="items-baseline">
                                            <Typography
                                                variant="heading2.1"
                                                color="grey-dark"
                                                className="pl-4 text-left"
                                            >
                                                {todaysDayName}
                                            </Typography>
                                            <Typography variant="heading3.2" color="brand-main">
                                                {format(new Date(), 'dd/MM/yyyy')}
                                            </Typography>
                                        </RowContainer>
                                    </th>
                                    <TableHeader>08:00</TableHeader>
                                    <TableHeader>09:00</TableHeader>
                                    <TableHeader>10:00</TableHeader>
                                    <TableHeader>11:00</TableHeader>
                                    <TableHeader>12:00</TableHeader>
                                    <TableHeader>13:00</TableHeader>
                                    <TableHeader>14:00</TableHeader>
                                    <TableHeader>15:00</TableHeader>
                                    <TableHeader>16:00</TableHeader>
                                    <TableHeader>17:00</TableHeader>
                                    <TableHeader>18:00</TableHeader>
                                    <TableHeader>19:00</TableHeader>
                                    <TableHeader>20:00</TableHeader>
                                </tr>
                            </thead>
                            <tbody>
                                {(teamMembers || [])
                                    .filter((member) => !!member[0])
                                    .map((member) => {
                                        // member logs as an array of values
                                        const email = member[0];
                                        const teamMember = schedule[email];
                                        const name = `${member[1]} ${member[2]}`.trim();
                                        const role =
                                            allUsers.length > 0
                                                ? allUsers?.find((u) => u.email === email)?.role
                                                : '';

                                        const todaysDayNumber = new Date().getDay();
                                        const itinerary = teamMember
                                            ? teamMember.itinerary || []
                                            : [];
                                        const dayItinerary = itinerary
                                            .filter(
                                                (i) =>
                                                    i.day === todaysDayNumber &&
                                                    i.time >= dayStart &&
                                                    i.time < dayEnd
                                            )
                                            .sort((i) => i.time);

                                        return (
                                            <tr key={member} className="h-14">
                                                {/* Team member column - name, role & img */}
                                                <th className="border-r border-b border-table-border py-2 px-4 text-left font-normal">
                                                    <RowContainer
                                                        spacing="medium"
                                                        className="items-center"
                                                    >
                                                        <Link to={`/users/${email}`}>
                                                            <Avatar
                                                                name={name || email}
                                                                heightClass="h-10"
                                                                widthClass="w-10"
                                                            />
                                                        </Link>
                                                        <RowContainer className="flex-grow items-center justify-between">
                                                            <ColContainer>
                                                                <Link to={`/users/${email}`}>
                                                                    <Typography variant="heading3.1">
                                                                        {name}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="heading4"
                                                                        color="grey-dark"
                                                                    >
                                                                        {role}
                                                                    </Typography>
                                                                </Link>
                                                            </ColContainer>
                                                            <IconComponent
                                                                onClick={() =>
                                                                    removeUserFromGroup(team, email)
                                                                }
                                                                className="cursor-pointer text-grey-normal transition-all hover:text-error"
                                                                height="14"
                                                                icon="bin"
                                                            />
                                                        </RowContainer>
                                                    </RowContainer>
                                                </th>
                                                {/* Schedule cells that show schedule changes along the x axis */}
                                                {!dayItinerary || !dayItinerary.length ? (
                                                    // If there are no entries for the entire day we show an empty row of cells
                                                    <>
                                                        {Array.apply(
                                                            null,
                                                            Array((dayEnd - dayStart) / 15)
                                                        ).map((_cell, index) => (
                                                            <td
                                                                key={`empty${index}`}
                                                                className="border border-table-border bg-grey-light"
                                                            />
                                                        ))}
                                                    </>
                                                ) : (
                                                    // If there are values we take the previous days last entry and display that until
                                                    // todays first entry that is generated through schedule cells
                                                    <>
                                                        <PrevDayLatestCell
                                                            teamMember={teamMember}
                                                            routingProfiles={routingProfiles}
                                                            instances={instances}
                                                            dayStart={dayStart}
                                                            dayEnd={dayEnd}
                                                            itinerary={itinerary}
                                                            dayItinerary={dayItinerary.map(
                                                                (i) => i.time
                                                            )}
                                                            todaysDayNumber={todaysDayNumber}
                                                        />
                                                        <ScheduleCells
                                                            teamMember={teamMember}
                                                            routingProfiles={routingProfiles}
                                                            instances={instances}
                                                            dayEnd={dayEnd}
                                                            dayItinerary={dayItinerary}
                                                        />
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                )}
                {teamMembersNotInList && teamMembersNotInList.length ? (
                    <ColContainer className="w-72 pt-6">
                        {team ? (
                            <SelectList
                                placeholder="Add a user to this team"
                                options={teamMembersNotInList.map((m) => ({
                                    value: m.email,
                                    label: `${m.firstname} ${m.surname}`,
                                }))}
                                onChange={(e) => addUserToGroup(team, e.value)}
                            />
                        ) : (
                            <></>
                        )}
                    </ColContainer>
                ) : (
                    <></>
                )}
            </div>
        </>
    );
};

const TableHeader = ({ children }) => {
    return (
        <th className="border-b border-r border-table-border bg-grey-light" colSpan={4}>
            <Typography variant="heading2.1" color="grey-dark">
                {children}
            </Typography>
        </th>
    );
};

export default TeamScheduleBreakdown;
